/**
 * Keyboard and clipboard conventions shared by every sheet (#911, epic #872).
 *
 * Pure functions with no React and no DOM ownership, so both features get the
 * same answers to "did the caret reach the edge?" and "is this a grid paste or
 * ordinary typing?" — and so those answers can be unit-tested without a browser.
 */

type CaretTarget = { selectionStart: number; selectionEnd: number; value: string };

/**
 * The caret positions of a text control, or null when there is no caret.
 *
 * Duck-typed rather than `instanceof HTMLInputElement` on purpose: the web unit
 * suite runs in a plain Node environment where those constructors do not exist,
 * so an instanceof check silently reported EVERY control as having no caret —
 * which would have made these rules untestable while looking like they passed.
 * Controls with no text caret (a select, a checkbox, a button) report a null
 * `selectionStart` or no string value and fall through to null here, which is
 * the answer their callers want anyway.
 */
function caretTarget(target: EventTarget | null): CaretTarget | null {
  const candidate = target as Partial<CaretTarget> | null;
  if (!candidate || typeof candidate.value !== "string") return null;
  if (typeof candidate.selectionStart !== "number" || typeof candidate.selectionEnd !== "number") return null;
  return candidate as CaretTarget;
}

/**
 * True when a left-arrow should LEAVE the cell rather than move the caret.
 *
 * Non-text controls (a select, a toggle button) report true: there is no caret
 * to move inside them, so an arrow key there is unambiguously navigation.
 */
export function atCellStart(target: EventTarget | null): boolean {
  const input = caretTarget(target);
  if (!input) return true;
  return input.selectionStart === 0 && input.selectionEnd === 0;
}

/** Mirror of `atCellStart` for the right edge. */
export function atCellEnd(target: EventTarget | null): boolean {
  const input = caretTarget(target);
  if (!input) return true;
  return input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
}

/**
 * Parses a clipboard payload into a rectangular block of cell values.
 *
 * Spreadsheets separate cells with tabs and rows with newlines, so a payload
 * with neither is indistinguishable from ordinary typing and is left to the
 * input (returns null). Taking over a single-value paste would break pasting a
 * name into one cell, which is by far the commonest paste in either sheet.
 */
export function parseSheetPaste(text: string): string[][] | null {
  // Line endings are normalized BEFORE the single-value check, not after: a
  // classic-Mac copy separates rows with a bare \r, which the check would
  // otherwise see as neither a tab nor a newline and hand back to the input as
  // ordinary typing.
  const normalized = text.replace(/\r\n?/g, "\n");
  if (!normalized.includes("\t") && !normalized.includes("\n")) return null;
  const rows = normalized.split("\n").map((line) => line.split("\t"));
  // Drop trailing rows that carry no VALUE, not merely trailing newlines.
  //
  // A spreadsheet serializes a selection whose last row is empty as delimiters
  // with nothing between them — a 2x2 range ending in a blank row copies as
  // "Milk\t2\n\t". Stripping only /\n+$/ leaves that "\t" alive as a genuine
  // row of two empty cells, which the paste then writes over the row BELOW the
  // block: it blanks that row's cells, and in Shopping a cleared name is a
  // delete (see the `clears` branch of ListSheet's flush), so the row is
  // removed from the server. main's handleCellPaste popped every trailing line
  // that was blank or whitespace-only; that trimming has to survive the move
  // into the shared parser.
  while (rows.length && (rows[rows.length - 1] ?? []).every((cell) => !cell.trim())) rows.pop();
  if (!rows.length || (rows.length === 1 && (rows[0]?.length ?? 0) <= 1)) return null;
  return rows;
}

export type SheetNavigation =
  | { kind: "move"; rowDelta: number; columnDelta: number }
  | { kind: "append-row" }
  | { kind: "revert-row" }
  | null;

/**
 * The one place both sheets decide what a keystroke means.
 *
 * Enter steps DOWN rather than committing: in a grid, Enter is how bulk typing
 * flows from row to row, and a commit bound to it would fire a save on every
 * line. Left/right only leave the cell from its edges, so ordinary caret
 * movement inside a value still works. Escape reverts the row — the single
 * most-wanted undo in a spreadsheet.
 */
export function sheetNavigationFor(event: {
  key: string;
  shiftKey: boolean;
  target: EventTarget | null;
}, position: { isLastRow: boolean }): SheetNavigation {
  if (event.key === "Escape") return { kind: "revert-row" };
  if (event.key === "Enter") return position.isLastRow ? { kind: "append-row" } : { kind: "move", rowDelta: 1, columnDelta: 0 };
  if (event.key === "ArrowDown" && !event.shiftKey) return { kind: "move", rowDelta: 1, columnDelta: 0 };
  if (event.key === "ArrowUp" && !event.shiftKey) return { kind: "move", rowDelta: -1, columnDelta: 0 };
  if (event.key === "ArrowRight" && atCellEnd(event.target)) return { kind: "move", rowDelta: 0, columnDelta: 1 };
  if (event.key === "ArrowLeft" && atCellStart(event.target)) return { kind: "move", rowDelta: 0, columnDelta: -1 };
  return null;
}
